import * as React from 'react';
import styled, { css } from 'styled-components';

import { getSemanticAttributes } from '../../internals/semanticProps';
import { rem } from '../../tokens/spacing';
import { palette } from '../../tokens/palette';

export type SurfaceVariant = 'default' | 'elevated' | 'inset';

export type SurfaceTone = 'neutral' | 'primary' | 'success' | 'error' | 'info' | 'dark';

const TONE_MAP: Record<Exclude<SurfaceTone, 'neutral'>, { bg: string; border: string }> = {
  primary: { bg: palette.LightPurple, border: palette.LightPurple },
  success: { bg: palette.LightGreen, border: palette.LightGreen },
  error: { bg: palette.LightRed, border: palette.LightRed },
  info: { bg: palette.Magnolia, border: palette.Magnolia },
  dark: { bg: palette.OffWhite, border: palette.OffWhite },
};

export interface SurfaceProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: SurfaceVariant;
  /** Semantic color tone — overrides background and border color. */
  tone?: SurfaceTone;
  bordered?: boolean;
  padding?: string;
  height?: string | number;
  hasOverFlow?: boolean;
  semanticId?: string;
}

const StyledSurface = styled.div.withConfig({
  shouldForwardProp: (prop) =>
    !['$variant', '$tone', '$bordered', '$padding', '$height', '$hasOverFlow'].includes(String(prop)),
})<{
  $variant: SurfaceVariant;
  $tone: SurfaceTone;
  $bordered: boolean;
  $padding: string;
  $height?: string | number;
  $hasOverFlow?: boolean;
}>`
  box-sizing: border-box;
  background: var(--ui-surface);
  border: ${rem(1)} solid var(--ui-border-muted);
  border-radius: var(--ui-radius-md, ${rem(6)});
  padding: ${({ $padding }) => $padding};

  ${({ $height }) =>
    $height != null &&
    css`
      min-height: ${typeof $height === 'number' ? rem($height) : $height};
      overflow-y: auto;
    `}

  ${({ $bordered }) =>
    $bordered &&
    css`
      border-bottom: 1px solid var(--ui-background-muted);
    `}

  ${({ $variant }) => {
    switch ($variant) {
      case 'elevated':
        return css`
          box-shadow: ${rem(0)} ${rem(4)} ${rem(12)}
            var(--ui-shadow-default, rgba(203, 186, 201, 0.12));
        `;
      case 'inset':
        return css`
          background: var(--ui-background-muted);
        `;
      case 'default':
      default:
        return css``;
    }
  }}

  ${({ $tone }) => {
    if ($tone === 'neutral') return css``;
    const colors = TONE_MAP[$tone];
    return css`
      background: ${colors.bg};
      border-color: ${colors.border};
    `;
  }}

  ::-webkit-scrollbar {
    height: 5px;
    width: 5px;
  }

  ::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: var(--ui-radius-md, 7px);
  }

  ::-webkit-scrollbar-thumb {
    background: var(--ui-text-subtle);
    border-radius: var(--ui-radius-md, 7px);
  }

  ::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
`;

export const Surface = React.forwardRef<HTMLDivElement, SurfaceProps>(function Surface(
  {
    variant = 'default',
    tone = 'neutral',
    bordered = false,
    padding = rem(16),
    height,
    hasOverFlow,
    semanticId,
    ...rest
  },
  ref
) {
  const semanticProps = getSemanticAttributes(semanticId);

  return (
    <StyledSurface
      ref={ref}
      $variant={variant}
      $tone={tone}
      $bordered={bordered}
      $padding={padding}
      $height={height}
      $hasOverFlow={hasOverFlow}
      data-variant={variant}
      data-tone={tone}
      {...semanticProps}
      {...rest}
    />
  );
});

Surface.displayName = 'Surface';
